import { CurrencyPipe, DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { HorizontalListData, HorizontalListFactory, LoanInfoDto, ProductCode } from "common";
import { AccountData, AccountStateDto, AccountStateWidgetData } from "../account.model";

@Injectable()
export class AccountStateWidgetAdapter {
  constructor(
    private readonly datePipe: DatePipe,
    private readonly currencyPipe: CurrencyPipe,
  ) { }

  adaptListData = (accountState: AccountStateWidgetData, account: AccountData): HorizontalListData[] => {
    const loanInfo: LoanInfoDto = account?.loanInfo;
    const stateData: AccountStateDto = accountState?.stateData;
    const isLCF = account.productCode === ProductCode.Law;

    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Available funds',
        displayedValue: this.formatCurrency(loanInfo?.loanAvailableFunds) || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'MSA available funds',
        displayedValue: this.formatCurrency(loanInfo?.msaAvailableFunds) || '-',
        isItemHidden: !isLCF
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Pay off amount',
        displayedValue: this.formatCurrency(stateData?.principalBalance + stateData?.accruedInterest) || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last draw',
        displayedValue: this.formatDateCurrency(stateData?.lastDrawDate, stateData?.lastDrawAmount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'First draw',
        displayedValue: this.formatDateCurrency(stateData?.firstDrawDate, stateData?.firstDrawAmount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last auto-payment',
        displayedValue: this.formatDateCurrency(stateData?.lastAutoPaymentDate, stateData?.lastAutoPaymentAmount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last pre-payment',
        displayedValue: this.formatDateCurrency(stateData?.lastManualPaymentDate, stateData?.lastManualPaymentAmount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Fee balance',
        displayedValue: this.formatCurrency(stateData?.feeBalance),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Total draw fees',
        displayedValue: this.formatCurrency(stateData?.totalFees),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Total interest',
        displayedValue: this.formatCurrency(stateData?.totalInterestCharged),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Total interest paid',
        displayedValue: this.formatCurrency(stateData?.totalInterestPaid),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Total principal paid',
        displayedValue: this.formatCurrency(stateData?.totalPrincipalPaid),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Cycle start date',
        displayedValue: this.formatDate(stateData?.cycleStartDate),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Cycle start day',
        displayedValue: stateData?.cycleStartDay,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Repayment term override',
        displayedValue: this.getRepaymentTermOverrideValue(account?.repaymentTermOverride)
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last re-amortization date',
        displayedValue: this.formatDate(stateData?.lastReAmortizationDate),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last maintenance date',
        displayedValue: this.formatDate(account?.lastMaintenanceDate),
      }),
    ]
  }

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value, "$");
  }

  private formatDate(value: Date): string {
    return this.datePipe.transform(value);
  }

  private formatDateCurrency(date: Date, value: number): string {
    const formattedDate = this.formatDate(date);
    const formattedCurrency = this.formatCurrency(value);
    return `${formattedDate} - ${formattedCurrency}`;
  }

  private getRepaymentTermOverrideValue(repaymentTermOverride: any): string {
    return repaymentTermOverride ? `${repaymentTermOverride} months` : '-';
  }
}